import type { KlearDate } from '@klear/date'
import { Timestamp } from '@klear/time'
import { Money } from '@klear/money'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import type { StripeError, StripeInboundTransfer, StripeOutboundPayment } from '@klear/stripe'
import keys from 'lodash/keys'
import {
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_SCHEDULED,
  normalizePaymentStatus,
  type PaymentStatus,
  PAYMENT_TYPE_SUPPLIER,
  PAYMENT_TYPE_READABLE_NAMES,
  PaymentNetworkOptions,
  PaymentTypeOptions,
  STRIPE_ID_PREFIXES,
  EXTERNAL_PAYMENT_PROVIDER_LABELS,
  type ExternalPaymentProvider,
  type PaymentAccountType,
  type PaymentNetwork,
  type PaymentType
} from '@klear/payment/constants'
import { paymentStatusLabel } from '@klear/payment/presentation'
import { paymentCanAmend } from '@klear/payment/status'
import type { PaymentAccountDetails } from '@klear/payment/schema'

type StripePaymentLike = StripeOutboundPayment | StripeInboundTransfer | { id: string; status: string; object?: string }

type StripeReceiptFields = { hosted_regulatory_receipt_url?: string }

export interface LinkedOrderEventDisplay {
  idDisplay?: string
  orderIdDisplay?: string
}

export interface OrderEventForPayment {
  id?: number
  orderId?: number
  type?: string
  idDisplay?: string
  orderIdDisplay?: string
  paymentType?: PaymentType
  paymentNetwork?: PaymentNetwork
  effectiveDate?: KlearDate
  amount: Money
  internalNotes?: string
  notes?: string
  statementDescriptor?: string
  sourceAccountType?: PaymentAccountType
  sourceAccountId?: number
  sourceAccountOrganizationId?: number
  sourceAccountOrganizationName?: string
  destinationAccountType?: PaymentAccountType
  destinationAccountId?: number
  destinationAccountOrganizationId?: number
  destinationAccountOrganizationName?: string
  stripePayment?: StripePaymentLike
  stripePaymentId?: string
  stripePaymentError?: StripeError
  paymentStatus?: string
  stripeDestinationLast4?: string
  createdByUserId?: number
  createdByUserEmail?: string
  createdByUserFirstName?: string
  createdByUserLastName?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  createdAt?: Timestamp | null
  updatedAt?: Timestamp | null
}

export type PaymentJson = {
  id?: number
  idDisplay?: string
  token?: string
  paymentType?: PaymentType
  paymentNetwork?: PaymentNetwork
  effectiveDate?: KlearDate
  amount?: Money
  currency?: CurrencyCode
  internalNotes?: string
  notes?: string
  statementDescriptor?: string
  notifySupplier?: boolean
  number?: string
  orderEventId?: number
  operationalEventId?: number
  payableId?: number
  fundingExternalAccountId?: number
  fundingExternalTransferId?: string
  externalProvider?: string | null
  externalReference?: string | null
  last4?: string
  orderId?: number
  orderEventType?: string
  orderEventIdDisplay?: string
  orderIdDisplay?: string
  stripePayment?: StripePaymentLike
  stripePaymentId?: string
  stripePaymentError?: StripeError
  status?: PaymentStatus
  creditedAt?: Timestamp
  scheduledDate?: KlearDate
  sendAfterPaymentId?: number
  sourceAccountType?: PaymentAccountType
  sourceAccountId?: number
  sourceAccountOrganizationId?: number
  sourceAccountOrganizationName?: string
  destinationAccountType?: PaymentAccountType
  destinationAccountId?: number
  destinationAccountOrganizationId?: number
  destinationAccountOrganizationName?: string
  linkedOrderEventDisplays?: LinkedOrderEventDisplay[]
  createdAt?: Timestamp
  updatedAt?: Timestamp
  deletedAt?: Timestamp
  createdByUserId?: number
  createdByUserEmail?: string
  createdByUserFirstName?: string
  createdByUserLastName?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  updatedByUserFirstName?: string
  updatedByUserLastName?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
  deletedByUserFirstName?: string
  deletedByUserLastName?: string
}

export class Payment {
  static readonly __klearClass = 'Payment'

  id: number
  idDisplay: string
  token?: string
  paymentType?: PaymentType
  paymentNetwork?: PaymentNetwork
  effectiveDate?: KlearDate
  amount: Money
  internalNotes?: string
  notes?: string
  statementDescriptor?: string
  notifySupplier: boolean
  number?: string
  orderEventId?: number
  operationalEventId?: number
  payableId?: number
  fundingExternalAccountId?: number
  fundingExternalTransferId?: string
  externalProvider?: string | null
  externalReference?: string | null
  last4?: string
  orderId?: number
  orderEventType?: string
  orderEventIdDisplay?: string
  orderIdDisplay?: string
  stripePayment?: StripePaymentLike
  stripePaymentId?: string
  stripePaymentError?: StripeError
  status: PaymentStatus
  creditedAt?: Timestamp
  scheduledDate?: KlearDate
  sendAfterPaymentId?: number
  sourceAccountType: PaymentAccountType
  sourceAccountId: number
  sourceAccountOrganizationId: number
  sourceAccountOrganizationName: string
  destinationAccountType: PaymentAccountType
  destinationAccountId: number
  destinationAccountOrganizationId: number
  destinationAccountOrganizationName: string
  linkedOrderEventDisplays: LinkedOrderEventDisplay[]
  createdAt?: Timestamp
  updatedAt?: Timestamp
  deletedAt?: Timestamp
  createdByUserId?: number
  createdByUserEmail?: string
  createdByUserFirstName?: string
  createdByUserLastName?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  updatedByUserFirstName?: string
  updatedByUserLastName?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
  deletedByUserFirstName?: string
  deletedByUserLastName?: string

  constructor(props: PaymentJson = {}) {
    this.id = props.id ?? 0
    this.idDisplay = props.idDisplay?.trim() || (this.id > 0 ? `#${this.id}` : '')
    this.token = props.token ?? undefined
    this.paymentType = props.paymentType
    this.paymentNetwork = props.paymentNetwork
    this.effectiveDate = props.effectiveDate
    this.amount = props.amount ?? Money.zero(props.currency ?? DEFAULT_CURRENCY_CODE)
    this.internalNotes = props.internalNotes
    this.notes = props.notes
    this.statementDescriptor = props.statementDescriptor
    this.notifySupplier = props.notifySupplier ?? false
    this.number = props.number || undefined
    this.orderEventId = props.orderEventId ?? props.orderEventId
    this.operationalEventId = props.operationalEventId
    this.payableId = props.payableId
    this.fundingExternalAccountId = props.fundingExternalAccountId
    this.fundingExternalTransferId = props.fundingExternalTransferId
    this.externalProvider = props.externalProvider
    this.externalReference = props.externalReference
    this.last4 = props.last4 || undefined
    this.orderId = props.orderId || undefined
    this.orderEventType = props.orderEventType || undefined
    this.orderIdDisplay = props.orderIdDisplay || undefined
    this.orderEventIdDisplay = props.orderEventIdDisplay || undefined
    this.stripePayment = props.stripePayment || undefined
    this.stripePaymentId = props.stripePaymentId || props.stripePayment?.id || undefined
    this.stripePaymentError = props.stripePaymentError || undefined
    this.status = normalizePaymentStatus(
      props.status ??
        (this.stripePaymentError && keys(this.stripePaymentError).length > 0
          ? PAYMENT_STATUS_FAILED
          : PAYMENT_STATUS_SCHEDULED)
    )
    this.scheduledDate = props.scheduledDate
    this.sendAfterPaymentId = props.sendAfterPaymentId
    this.sourceAccountType = props.sourceAccountType as PaymentAccountType
    this.sourceAccountId = props.sourceAccountId || 0
    this.sourceAccountOrganizationId = props.sourceAccountOrganizationId || 0
    this.sourceAccountOrganizationName = props.sourceAccountOrganizationName || ''
    this.destinationAccountType = props.destinationAccountType as PaymentAccountType
    this.destinationAccountId = props.destinationAccountId || 0
    this.destinationAccountOrganizationId = props.destinationAccountOrganizationId || 0
    this.destinationAccountOrganizationName = props.destinationAccountOrganizationName || ''
    this.linkedOrderEventDisplays = props.linkedOrderEventDisplays ?? []
    this.creditedAt = props.creditedAt
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.createdByUserFirstName = props.createdByUserFirstName
    this.createdByUserLastName = props.createdByUserLastName
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.updatedByUserFirstName = props.updatedByUserFirstName
    this.updatedByUserLastName = props.updatedByUserLastName
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
    this.deletedByUserFirstName = props.deletedByUserFirstName
    this.deletedByUserLastName = props.deletedByUserLastName
  }

  static fromOrderEvent(event: OrderEventForPayment) {
    return new Payment({
      id: event.id,
      orderId: event.orderId,
      orderEventId: event.id,
      orderEventType: event.type,
      orderEventIdDisplay: event.idDisplay,
      orderIdDisplay: event.orderIdDisplay,
      paymentType: event.paymentType,
      paymentNetwork: event.paymentNetwork,
      effectiveDate: event.effectiveDate,
      amount: event.amount,
      internalNotes: event.internalNotes,
      notes: event.notes,
      statementDescriptor: event.statementDescriptor,
      scheduledDate: event.effectiveDate,
      sourceAccountType: event.sourceAccountType!,
      sourceAccountId: event.sourceAccountId!,
      sourceAccountOrganizationId: event.sourceAccountOrganizationId!,
      sourceAccountOrganizationName: event.sourceAccountOrganizationName!,
      destinationAccountType: event.destinationAccountType!,
      destinationAccountId: event.destinationAccountId!,
      destinationAccountOrganizationId: event.destinationAccountOrganizationId!,
      destinationAccountOrganizationName: event.destinationAccountOrganizationName!,
      stripePayment: event.stripePayment,
      stripePaymentId: event.stripePaymentId || event.stripePayment?.id || undefined,
      stripePaymentError: event.stripePaymentError,
      status: normalizePaymentStatus(
        event.paymentStatus === 'Error' ? PAYMENT_STATUS_FAILED : (event.paymentStatus ?? PAYMENT_STATUS_SCHEDULED)
      ),
      last4: event.stripeDestinationLast4,
      createdByUserId: event.createdByUserId,
      createdByUserEmail: event.createdByUserEmail,
      createdByUserFirstName: event.createdByUserFirstName,
      createdByUserLastName: event.createdByUserLastName,
      updatedByUserId: event.updatedByUserId,
      updatedByUserEmail: event.updatedByUserEmail,
      createdAt: event.createdAt ?? undefined,
      updatedAt: event.updatedAt ?? undefined
    })
  }

  toOrderEventForPayment(options?: { orderEventId?: number }): OrderEventForPayment {
    const orderEventId = options?.orderEventId ?? this.orderEventId ?? this.orderEventId
    return {
      id: orderEventId,
      orderId: this.orderId,
      type: this.orderEventType,
      idDisplay: this.orderEventIdDisplay,
      orderIdDisplay: this.orderIdDisplay,
      paymentType: this.paymentType,
      paymentNetwork: this.paymentNetwork,
      effectiveDate: this.effectiveDate,
      amount: this.amount,
      internalNotes: this.internalNotes,
      notes: this.notes,
      statementDescriptor: this.statementDescriptor,
      sourceAccountType: this.sourceAccountType,
      sourceAccountId: this.sourceAccountId,
      sourceAccountOrganizationId: this.sourceAccountOrganizationId,
      sourceAccountOrganizationName: this.sourceAccountOrganizationName,
      destinationAccountType: this.destinationAccountType,
      destinationAccountId: this.destinationAccountId,
      destinationAccountOrganizationId: this.destinationAccountOrganizationId,
      destinationAccountOrganizationName: this.destinationAccountOrganizationName,
      stripePayment: this.stripePayment,
      stripePaymentId: this.stripePaymentId || this.stripePayment?.id || undefined,
      stripePaymentError: this.stripePaymentError,
      paymentStatus: this.status,
      stripeDestinationLast4: this.last4,
      createdByUserId: this.createdByUserId,
      createdByUserEmail: this.createdByUserEmail,
      createdByUserFirstName: this.createdByUserFirstName,
      createdByUserLastName: this.createdByUserLastName,
      updatedByUserId: this.updatedByUserId,
      updatedByUserEmail: this.updatedByUserEmail,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    }
  }

  get description() {
    const readableType = this.paymentType ? PAYMENT_TYPE_READABLE_NAMES[this.paymentType] : undefined
    const supplierSuffix =
      this.paymentType === PAYMENT_TYPE_SUPPLIER ? ` to ${this.destinationAccountOrganizationName}` : ''

    if (this.linkedOrderEventDisplays.length > 1 && readableType) {
      const displays = this.linkedOrderEventDisplays.map((e) => e.idDisplay).filter(Boolean)
      const shown = displays.slice(0, 2).join(', ')
      const remaining = displays.length - 2
      const suffix = remaining > 0 ? ` and ${remaining} other${remaining === 1 ? '' : 's'}` : ''
      return `${readableType}${supplierSuffix} for ${shown}${suffix}`
    }

    const singleLinked = this.linkedOrderEventDisplays[0]
    const orderEventIdDisplay = singleLinked?.idDisplay || this.orderEventIdDisplay
    const orderIdDisplay = singleLinked?.orderIdDisplay || this.orderIdDisplay

    if (orderEventIdDisplay && orderIdDisplay && readableType) {
      return `${readableType}${supplierSuffix} for ${orderIdDisplay}, ${orderEventIdDisplay}`
    }
    return `Klear pay from ${this.sourceAccountOrganizationName} to ${this.destinationAccountOrganizationName}`
  }

  get paymentTypeLabel() {
    return PaymentTypeOptions.find((option) => option.value === this.paymentType)?.label || ''
  }

  get canDelete(): boolean {
    return paymentCanAmend(this)
  }

  get statusLabel(): string {
    return paymentStatusLabel(this.status)
  }

  get paymentNetworkLabel() {
    return PaymentNetworkOptions.find((option) => option.value === this.paymentNetwork)?.label || ''
  }

  get paymentProviderName(): string {
    const paymentId = this.stripePaymentId ?? ''
    if (!paymentId) return 'Stripe'
    if (STRIPE_ID_PREFIXES.some((prefix) => paymentId.startsWith(prefix))) return 'Stripe'
    const underscoreIndex = paymentId.indexOf('_')
    if (underscoreIndex > 0) {
      const prefix = paymentId.substring(0, underscoreIndex) as ExternalPaymentProvider
      return EXTERNAL_PAYMENT_PROVIDER_LABELS[prefix] ?? prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase()
    }
    return 'Stripe'
  }

  get currency(): CurrencyCode {
    return this.amount.currency
  }

  get amountFormatted() {
    return this.amount.toString()
  }

  get stripeReceiptUrl(): string | undefined {
    return (this.stripePayment as StripeReceiptFields | undefined)?.hosted_regulatory_receipt_url ?? undefined
  }

  get stripePaymentErrorMessage(): string | undefined {
    if (keys(this.stripePaymentError || {}).length === 0) return undefined
    const error = this.stripePaymentError as { raw?: { message?: string }; message?: string }
    return error.raw?.message ?? error.message ?? 'Stripe Error'
  }

  get sourceAccountDetails(): PaymentAccountDetails {
    return {
      type: this.sourceAccountType!,
      id: this.sourceAccountId!,
      organizationId: this.sourceAccountOrganizationId!
    }
  }

  get destinationAccountDetails(): PaymentAccountDetails {
    return {
      type: this.destinationAccountType!,
      id: this.destinationAccountId!,
      organizationId: this.destinationAccountOrganizationId!
    }
  }

  get createdByEmail() {
    return this.createdByUserEmail?.trim() || 'Unknown'
  }

  isUnsaved() {
    return this.id === 0
  }

  toJSON(): PaymentJson {
    return {
      id: this.id,
      idDisplay: this.idDisplay,
      token: this.token,
      paymentType: this.paymentType,
      paymentNetwork: this.paymentNetwork,
      effectiveDate: this.effectiveDate,
      amount: this.amount,
      currency: this.amount.currency,
      internalNotes: this.internalNotes,
      notes: this.notes,
      statementDescriptor: this.statementDescriptor,
      notifySupplier: this.notifySupplier,
      number: this.number,
      orderEventId: this.orderEventId,
      operationalEventId: this.operationalEventId,
      payableId: this.payableId != null && this.payableId > 0 ? this.payableId : undefined,
      fundingExternalAccountId: this.fundingExternalAccountId,
      fundingExternalTransferId: this.fundingExternalTransferId,
      externalProvider: this.externalProvider,
      externalReference: this.externalReference,
      last4: this.last4,
      orderId: this.orderId,
      orderEventType: this.orderEventType,
      orderEventIdDisplay: this.orderEventIdDisplay,
      orderIdDisplay: this.orderIdDisplay,
      stripePayment: this.stripePayment,
      stripePaymentId: this.stripePaymentId,
      stripePaymentError: this.stripePaymentError,
      status: this.status,
      creditedAt: this.creditedAt,
      scheduledDate: this.scheduledDate,
      sendAfterPaymentId: this.sendAfterPaymentId,
      sourceAccountType: this.sourceAccountType,
      sourceAccountId: this.sourceAccountId,
      sourceAccountOrganizationId: this.sourceAccountOrganizationId,
      sourceAccountOrganizationName: this.sourceAccountOrganizationName,
      destinationAccountType: this.destinationAccountType,
      destinationAccountId: this.destinationAccountId,
      destinationAccountOrganizationId: this.destinationAccountOrganizationId,
      destinationAccountOrganizationName: this.destinationAccountOrganizationName,
      linkedOrderEventDisplays: this.linkedOrderEventDisplays,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      deletedAt: this.deletedAt,
      createdByUserId: this.createdByUserId,
      createdByUserEmail: this.createdByUserEmail,
      createdByUserFirstName: this.createdByUserFirstName,
      createdByUserLastName: this.createdByUserLastName,
      updatedByUserId: this.updatedByUserId,
      updatedByUserEmail: this.updatedByUserEmail,
      updatedByUserFirstName: this.updatedByUserFirstName,
      updatedByUserLastName: this.updatedByUserLastName,
      deletedByUserId: this.deletedByUserId,
      deletedByUserEmail: this.deletedByUserEmail,
      deletedByUserFirstName: this.deletedByUserFirstName,
      deletedByUserLastName: this.deletedByUserLastName
    }
  }
}
